import { tutorialTexts } from './tutorialTexts';
import { log } from '../utils/logger';

interface GameState {
  gameMode: 'tutorial' | 'normal';
  storySections: any[];
  showCards: boolean;
  showCardOfferModal: boolean;
  showStoryContent: boolean;
  backendStep: number;
  tutorialCompleted: boolean;
  isInActionSelection?: boolean;
  currentQueueIndex?: number;
  sectionQueue?: any[];
  typingComplete?: boolean;
  isShowingFirstParagraph?: boolean;
  isFullScreenCardMode?: boolean;
}

interface TutorialBubble {
  message: string;
  key?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  targetElement?: string;
  avatar?: string;
  buttonType?: 'continue' | 'dismiss' | 'none';
  requiresClick?: boolean;
  readinessStrategy?: 'simple' | 'animation' | 'boundingBoxStability' | 'staggeredList';
  delay?: number;
}

export function getTutorialGuidance(gameState: GameState): TutorialBubble | null {
  // No tutorial for normal mode or completed tutorial
  if (gameState.gameMode !== 'tutorial' || gameState.tutorialCompleted) {
    log.debug('[getTutorialGuidance] Skipping - gameMode:', gameState.gameMode, 'tutorialCompleted:', gameState.tutorialCompleted);
    return null;
  }

  const { storySections, showCards, showCardOfferModal, showStoryContent, backendStep } = gameState;
  
  log.debug('[getTutorialGuidance] Checking conditions - backendStep:', backendStep, 'showStoryContent:', showStoryContent, 'showCards:', showCards, 'currentQueueIndex:', gameState.currentQueueIndex);

  // Step 1: First card selection guidance (when cards are visible but not in action selection)
  if (showCards && backendStep === 1 && !gameState.isInActionSelection) {
    // Use different selectors based on card mode
    // In full-screen mode, target the first playable card directly
    const targetElement = gameState.isFullScreenCardMode
      ? '.playable-card'
      : '.hand-container .hand .card';
    return {
      message: tutorialTexts.pickCard,
      key: 'pick-card',
      position: 'top',
      targetElement,
      avatar: 'scientist',
      buttonType: 'none',
      readinessStrategy: 'simple'
    };
  }

  // Step 1: Action selection guidance (when action phrases are showing)
  if (showCards && backendStep === 1 && gameState.isInActionSelection) {
    // Use different selectors based on card mode
    // In full-screen mode, target the first suggested phrase button
    const targetElement = gameState.isFullScreenCardMode
      ? '.card-detail-phrase'
      : '.suggested-phrases ul li:first-child';
    return {
      message: tutorialTexts.pickAction,
      key: 'pick-action',
      position: 'bottom',
      targetElement,
      avatar: 'scientist',
      buttonType: 'none',
      readinessStrategy: 'simple'
    };
  }

  // Step 2: Card offering modal
  if (showCardOfferModal && backendStep === 2) {
    return {
      message: tutorialTexts.cardOffering,
      key: 'card-offering',
      position: 'top',
      targetElement: '.offered-card',
      avatar: 'scientist',
      buttonType: 'none',
      readinessStrategy: 'simple'
    };
  }

  // Step 3: Multi-card offering (yell + say) — teach multi-select
  if (showCardOfferModal && backendStep === 3) {
    return {
      message: tutorialTexts.cardOfferingMulti,
      key: 'card-offering-multi',
      position: 'top',
      targetElement: '.selection-limit-badge',
      avatar: 'scientist',
      buttonType: 'none',
      readinessStrategy: 'simple'
    };
  }

  // Step 2: Multiple cards guidance (after card offering, but not when in action selection)
  if (showCards && backendStep === 2 && !gameState.isInActionSelection) {
    // Use different selectors based on card mode
    // In full-screen mode, target the last (newest) card
    const targetElement = gameState.isFullScreenCardMode
      ? '.playable-card:last-child'
      : '.hand-container .hand .card';
    return {
      message: tutorialTexts.multipleCards,
      key: 'multiple-cards',
      position: 'top',
      targetElement,
      avatar: 'scientist',
      buttonType: 'none',
      readinessStrategy: 'simple'
    };
  }

  // Steps 3 & 4: Regular gameplay - no guidance needed

  // Step 4: Dice introduction (when showing first paragraph after playing card)
  if (backendStep === 4 && gameState.isShowingFirstParagraph) {
    return {
      message: tutorialTexts.diceIntroduction,
      key: 'dice-introduction',
      position: 'top',
      targetElement: '.next-button',
      avatar: 'scientist',
      buttonType: 'none',
      readinessStrategy: 'simple',
      delay: 1000
    };
  }

  // Step 4: Typing complete guidance (after typing animation finishes)
  if (showCards && backendStep === 4 && gameState.isInActionSelection && gameState.typingComplete) {
    // Use different selectors based on card mode
    const targetElement = gameState.isFullScreenCardMode
      ? '.card-detail-play-btn'
      : '.submit-action-btn';
    return {
      message: tutorialTexts.typingComplete,
      key: 'typing-complete',
      position: 'top',
      targetElement,
      avatar: 'scientist',
      buttonType: 'none',
      readinessStrategy: 'simple'
    };
  }

  // Terminal Step: Free-form input guidance (only when input is visible, before typing starts)
  if (showCards && backendStep === 4 && gameState.isInActionSelection && !gameState.typingComplete) {
    // Use different selectors based on card mode
    const targetElement = gameState.isFullScreenCardMode
      ? '.card-detail-input'
      : '.custom-phrase input';
    return {
      message: tutorialTexts.freeForm,
      key: 'free-form',
      position: 'top',
      targetElement,
      avatar: 'scientist',
      buttonType: 'none',
      readinessStrategy: 'simple',
      delay: 1500
    };
  }

  // Tutorial completion
  if (gameState.tutorialCompleted) {
    return {
      message: tutorialTexts.completed,
      position: 'bottom',
      avatar: 'scientist',
      buttonType: 'dismiss'
    };
  }

  return null;
}
